/* eslint-disable */
import { useRef, useState, useEffect } from 'react';
import { DeleteOutline } from '@mui/icons-material';
import RichTextBox from './RichTextBox';

export const DEFAULT_CELL = {
    content: '',
    fontSize: 12,
    fontFamily: '0',
    color: '#000000',
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isHighlight: false,
};

export const buildCells = (rows, cols, existing = []) => {
    const r = Math.max(1, rows || 1);
    const c = Math.max(1, cols || 1);
    return Array.from({ length: r }, (_, ri) =>
        Array.from({ length: c }, (_, ci) => ({
            ...DEFAULT_CELL,
            ...(existing?.[ri]?.[ci] || {}),
        }))
    );
};

const buildSizes = (n, existing) => {
    const count = Math.max(1, n || 1);
    if (Array.isArray(existing) && existing.length === count) {
        const sum = existing.reduce((a, b) => a + (Number(b) || 0), 0) || 1;
        return existing.map((v) => (Number(v) || 0) / sum); // normalize to 1
    }
    return Array.from({ length: count }, () => 1 / count);
};

const MIN_RATIO = 0.05;

const LabelTable = (props) => {
    const {
        rows = 2,
        cols = 2,
        cells: cellsProp,
        colWidths: colWidthsProp,
        rowHeights: rowHeightsProp,
        scale = 1,
        style = {},
        onClick,
        onDoubleClick,
        onChange,
        onLayoutChange,
        onCellSelect,
        onDelete,
        deleteSize = 24,
        previewMode = false,
        isEditing = false,
        selected = false,
        borderStyle,
        thickness,
        borderColor,
    } = props;

    // Row/column line handles are available whenever the table is selected or
    // being edited (but never in preview). This lets the user grab an internal
    // line to resize a row/column, while the rest of the table still moves and
    // the outer border (via Rnd) moves/resizes the whole table.
    const showResizeHandles = !previewMode && (isEditing || selected);

    const gridBorder = borderStyle && borderStyle !== 'none'
        ? `${Number(thickness) || 1}px ${borderStyle} ${borderColor || '#000000'}`
        : '1px solid #000';

    const wrapRef = useRef(null);
    const [cells, setCells] = useState(() => buildCells(rows, cols, cellsProp));
    const [colWidths, setColWidths] = useState(() => buildSizes(cols, colWidthsProp));
    const [rowHeights, setRowHeights] = useState(() => buildSizes(rows, rowHeightsProp));
    const [activeCell, setActiveCell] = useState(null);

    useEffect(() => {
        setCells(buildCells(rows, cols, cellsProp));
    }, [rows, cols, cellsProp]);
    useEffect(() => {
        setColWidths(buildSizes(cols, colWidthsProp));
    }, [cols, colWidthsProp]);
    useEffect(() => {
        setRowHeights(buildSizes(rows, rowHeightsProp));
    }, [rows, rowHeightsProp]);

    const handleCellChange = (r, c, value) => {
        setCells((prev) => {
            const next = prev.map((row, ri) =>
                ri === r
                    ? row.map((cell, ci) => (ci === c ? { ...cell, content: value } : cell))
                    : row
            );
            onChange?.(next);
            return next;
        });
    };

    const startResize = (axis, index, e) => {
        e.preventDefault();
        e.stopPropagation();
        const isCol = axis === 'col';
        const startPos = isCol ? e.clientX : e.clientY;
        const totalPx = (isCol ? wrapRef.current?.offsetWidth : wrapRef.current?.offsetHeight) || 1;
        const startSizes = isCol ? [...colWidths] : [...rowHeights];

        const onMove = (ev) => {
            const cur = isCol ? ev.clientX : ev.clientY;
            const deltaRatio = (cur - startPos) / (totalPx * (scale || 1));
            let a = startSizes[index] + deltaRatio;
            let b = startSizes[index + 1] - deltaRatio;
            if (a < MIN_RATIO || b < MIN_RATIO) return;
            const next = [...startSizes];
            next[index] = a;
            next[index + 1] = b;
            if (isCol) {
                setColWidths(next);
                onLayoutChange?.({ colWidths: next });
            } else {
                setRowHeights(next);
                onLayoutChange?.({ rowHeights: next });
            }
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    const cumulative = (arr, i) => arr.slice(0, i + 1).reduce((a, b) => a + b, 0);

    // rotate (transform) ko wrapper par lagate hain taaki delete button bhi table ke
    // saath rotate ho; baaki style (border/shadow/radius) table par hi rehti hai.
    const { transform, ...tableStyle } = style || {};
    return (
        <div
            ref={wrapRef}
            className='group'
            style={{ position: 'relative', width: '100%', height: '100%', transform: transform || 'none' }}
        >
            {!previewMode && onDelete && (
                <button
                    type='button'
                    title='Delete'
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    style={{ width: deleteSize, height: deleteSize }}
                    className='label-text-input absolute -top-3 -right-3 z-30 hidden group-hover:flex items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600'
                >
                    <DeleteOutline sx={{ fontSize: Math.round(deleteSize * 0.6) }} />
                </button>
            )}
            <table
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                style={{
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                    width: '100%',
                    height: '100%',
                    cursor: isEditing ? 'default' : 'move',
                    ...tableStyle,
                }}
            >
                <colgroup>
                    {colWidths.map((w, i) => (
                        <col key={i} style={{ width: `${w * 100}%` }} />
                    ))}
                </colgroup>
                <tbody>
                    {cells.map((row, r) => (
                        <tr key={r} style={{ height: `${(rowHeights[r] || 1 / cells.length) * 100}%` }}>
                            {row.map((cell, c) => {
                                const isActive =
                                    activeCell?.r === r && activeCell?.c === c;
                                const cellFontSize = Number(cell.fontSize) || 12;
                                const cellRotate = Number(cell.rotate) || 0;
                                const cellRadius = Number(cell.borderRadius) || 0;
                                const hasCustomBorder =
                                    cell.borderStyle && cell.borderStyle !== 'none';
                                const cellBorder = hasCustomBorder
                                    ? `${Number(cell.thickness) || 1}px ${cell.borderStyle} ${cell.borderColor || '#000000'}`
                                    : 'none';
                                // highlight = reverse print (black box + white text).
                                const cellHighlight = !!cell.isHighlight;
                                return (
                                    <td
                                        key={c}
                                        style={{
                                            border: gridBorder,
                                            padding: 0,
                                            verticalAlign: 'top',
                                            background: cellHighlight ? '#000000' : 'transparent',
                                            boxShadow:
                                                isActive && isEditing
                                                    ? 'inset 0 0 0 2px #4a70fb'
                                                    : 'none',
                                        }}
                                    >
                                        <RichTextBox
                                            value={cell.content ?? ''}
                                            editing={isEditing}
                                            className={`${isEditing ? 'label-text-input' : ''} label-richtext ${cellHighlight ? 'label-richtext-highlight' : ''}`}
                                            onClick={(e) => {
                                                if (isEditing) {
                                                    e.stopPropagation();
                                                    setActiveCell({ r, c });
                                                    onCellSelect?.(r, c, cell);
                                                }
                                            }}
                                            onChange={(html) =>
                                                handleCellChange(r, c, html)
                                            }
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                minWidth: 0,
                                                display: 'block',
                                                boxSizing: 'border-box',
                                                border: cellBorder,
                                                borderRadius: cellRadius ? `${cellRadius}px` : 0,
                                                outline: 'none',
                                                background: 'transparent',
                                                overflow: 'hidden',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                pointerEvents: isEditing ? 'auto' : 'none',
                                                cursor: isEditing ? 'text' : 'move',
                                                fontSize: cellFontSize,
                                                lineHeight: cell.lineHeight || 'normal',
                                                fontFamily: cell.fontFamily,
                                                color: cellHighlight ? '#ffffff' : cell.color,
                                                fontWeight: cell.isBold ? 'bold' : 'normal',
                                                fontStyle: cell.isItalic ? 'italic' : 'normal',
                                                textAlign: cell.textAlign || 'left',
                                                transform: cellRotate
                                                    ? `rotate(${cellRotate}deg)`
                                                    : 'none',
                                                transformOrigin: 'center',
                                            }}
                                        />
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>

            {showResizeHandles && colWidths.slice(0, -1).map((_, i) => (
                <div
                    key={`cdiv-${i}`}
                    className='label-text-input'
                    title='Drag to resize column'
                    onMouseDown={(e) => startResize('col', i, e)}
                    style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${cumulative(colWidths, i) * 100}%`,
                        width: 8,
                        marginLeft: -4,
                        cursor: 'col-resize',
                        zIndex: 6,
                    }}
                />
            ))}
            {showResizeHandles && rowHeights.slice(0, -1).map((_, i) => (
                <div
                    key={`rdiv-${i}`}
                    className='label-text-input'
                    title='Drag to resize row'
                    onMouseDown={(e) => startResize('row', i, e)}
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: `${cumulative(rowHeights, i) * 100}%`,
                        height: 8,
                        marginTop: -4,
                        cursor: 'row-resize',
                        zIndex: 6,
                    }}
                />
            ))}
        </div>
    );
};
export default LabelTable;
